import React, {useEffect, useRef, useState} from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Box, Container, Drawer, List, ListItem,
  ListItemIcon, ListItemText, IconButton, useMediaQuery, useTheme,
  BottomNavigation, BottomNavigationAction, Avatar, Menu, MenuItem,
  Divider, Button
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  Explore as ExploreIcon,
  Chat as ChatIcon,
  LibraryBooks as VocabularyIcon,
  AccountCircle as ProfileIcon,
  Logout as LogoutIcon,
  ChevronLeft as ChevronLeftIcon,
  History as HistoryIcon,
  Book as JournalIcon,
  AutoStories as BreadcrumbsIcon,
  Map as MapIcon,
  ExpandMore as ExpandMoreIcon,
  AdminPanelSettings as AdminIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { keyframes } from '@mui/material/styles';
import {useLanguage} from "../context/LanguageContext";
import { getLanguageByCode } from '../config/languagesConfig';
import magellanLogoSextant from './Magellan Languages-Transparent-02.png';
import apiClient from "../services/apiClient";
import Flag from 'react-world-flags';
import TutorAssistant from "./TutorAssistant";
import { TutorProvider } from "../context/TutorContext";

const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 rgba(198, 152, 43, 0.55); }
  70% { box-shadow: 0 0 0 10px rgba(198, 152, 43, 0); }
  100% { box-shadow: 0 0 0 0 rgba(198, 152, 43, 0); }
`;

const drawerWidth = 250;

// Language code -> ISO country for the flag glyph (covers es-419 / pt-BR).
const languageToCountry = {
  en: 'GB', fr: 'FR', es: 'ES', 'es-419': 'MX', de: 'DE',
  it: 'IT', pt: 'PT', 'pt-BR': 'BR', ro: 'RO', ja: 'JP',
};
const flagFor = (code) => languageToCountry[code] || languageToCountry[(code || '').split('-')[0]] || 'GB';

const Layout = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Publish the bottom nav's real height (icons, raised center button, border and
  // iPhone home-indicator padding) as --bottom-nav-height, so fixed bars on other
  // pages (journal toolbar, chat input) sit exactly on top of it. Unset = no nav.
  const bottomNavRef = useRef(null);
  useEffect(() => {
    const root = document.documentElement;
    const el = bottomNavRef.current;
    if (!isMobile || !el) {
      root.style.removeProperty('--bottom-nav-height');
      return undefined;
    }
    const update = () => root.style.setProperty('--bottom-nav-height', `${Math.ceil(el.getBoundingClientRect().height)}px`);
    update();
    let observer = null;
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(update);
      observer.observe(el);
    }
    window.addEventListener('resize', update);
    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('resize', update);
      root.style.removeProperty('--bottom-nav-height');
    };
  }, [isMobile]);
  const [railOpen, setRailOpen] = useState(true);        // desktop persistent rail
  const [menuOpen, setMenuOpen] = useState(false);        // mobile full-menu drawer
  const [anchorEl, setAnchorEl] = useState(null);

  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [languageMenuAnchorEl, setLanguageMenuAnchorEl] = useState(null);
  const { selectedLanguage, setSelectedLanguage, languageOptions } = useLanguage();

  const [hasAdminAccess, setHasAdminAccess] = useState(false);

  const handleLanguageMenuOpen = (e) => setLanguageMenuAnchorEl(e.currentTarget);
  const handleLanguageMenuClose = () => setLanguageMenuAnchorEl(null);
  const handleLanguageSelect = (code) => { setSelectedLanguage(code); handleLanguageMenuClose(); };

  const go = (path) => { navigate(path); setMenuOpen(false); };

  useEffect(() => {
    const check = async () => {
      try {
        const r = await apiClient.admin.checkAuthorization();
        setHasAdminAccess(r.data.is_admin);
      } catch (e) { setHasAdminAccess(false); }
    };
    if (user) check();
  }, [user]);

  // ---- Navigation model ----
  // Quick bar (mobile speed): Start is the hero; the three "ways to learn" are
  // Voyages, Conversations and Journal. Words lives on Home + the full menu.
  const speedNav = [
    { title: 'Home', path: '/', icon: <HomeIcon /> },
    { title: 'Voyages', path: '/breadcrumbs', icon: <BreadcrumbsIcon /> },
    { title: 'Start', path: '/explore', icon: <ExploreIcon />, center: true },
    { title: 'Logbook', path: '/sessions', icon: <HistoryIcon /> },
    { title: 'Journal', path: '/journal', icon: <JournalIcon /> },
  ];

  // Full menu (desktop rail + mobile ☰): everything, one tap away.
  const fullMenu = [
    { title: 'Home', path: '/', icon: <HomeIcon /> },
    { title: 'Voyages', path: '/breadcrumbs', icon: <BreadcrumbsIcon /> },
    { title: 'Start', path: '/explore', icon: <ExploreIcon /> },
    { title: 'Words', path: '/vocabulary', icon: <VocabularyIcon /> },
    { title: 'Journal', path: '/journal', icon: <JournalIcon /> },
    { title: 'Progress', path: '/progress', icon: <MapIcon /> },
    // The quick-bar "Chats" opens this same screen pre-filtered; the menu keeps
    // just "Logbook" (all sessions) to avoid duplication.
    { title: 'Logbook', path: '/sessions', icon: <HistoryIcon /> },
  ];

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  const currentTitle = () => {
    const match = [...fullMenu].sort((a, b) => b.path.length - a.path.length).find((i) => isActive(i.path));
    return match ? match.title : 'Magellan';
  };
  const speedValue = () => {
    const idx = speedNav.findIndex((i) => isActive(i.path));
    return idx >= 0 ? idx : 0;
  };

  const toggleRail = () => setRailOpen(!railOpen);
  const handleUserMenuOpen = (e) => setAnchorEl(e.currentTarget);
  const handleUserMenuClose = () => setAnchorEl(null);
  const handleLogout = () => { handleUserMenuClose(); logout(); };

  const centerIcon = (icon) => (
    <Box sx={{
      bgcolor: 'primary.main', borderRadius: '50%', p: 0.9,
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      color: 'white', animation: `${pulse} 2.4s infinite`,
    }}>
      {React.cloneElement(icon, { sx: { fontSize: 20 } })}
    </Box>
  );

  const NavList = ({ items }) => (
    <List>
      {items.map((item) => (
        <ListItem button key={item.title} onClick={() => go(item.path)} selected={isActive(item.path)}>
          <ListItemIcon>{item.icon}</ListItemIcon>
          <ListItemText primary={item.title} />
        </ListItem>
      ))}
    </List>
  );

  const languageMenu = (
    <Menu
      anchorEl={languageMenuAnchorEl}
      open={Boolean(languageMenuAnchorEl)}
      onClose={handleLanguageMenuClose}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      slotProps={{ paper: { sx: { minWidth: 280, borderRadius: 2 } } }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, fontFamily: '"Fraunces", Georgia, serif' }}>
          Language
        </Typography>
        <Typography variant="caption" color="text.secondary">Choose the language you're learning</Typography>
      </Box>
      <Divider />
      {languageOptions.length > 0 ? languageOptions.map((language) => {
        const cfg = getLanguageByCode(language.code);
        const selected = selectedLanguage === language.code;
        return (
          <MenuItem key={language.code} onClick={() => handleLanguageSelect(language.code)}
            selected={selected} sx={{ py: 1.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75, width: '100%' }}>
              <Flag code={flagFor(language.code)} style={{ width: 32, height: 24, borderRadius: 3, boxShadow: '0 0 0 1px rgba(0,0,0,0.08)' }} />
              <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                <Typography variant="body1" sx={{
                  fontWeight: selected ? 700 : 500,
                  color: selected ? 'primary.main' : 'text.primary', lineHeight: 1.2 }}>
                  {language.name}
                </Typography>
                {cfg?.nativeName && cfg.nativeName !== language.name && (
                  <Typography variant="caption" color="text.secondary">{cfg.nativeName}</Typography>
                )}
              </Box>
              <Typography variant="caption" sx={{ color: selected ? 'primary.main' : 'text.disabled', fontWeight: 700 }}>
                {selected ? '✓' : language.code.toUpperCase()}
              </Typography>
            </Box>
          </MenuItem>
        );
      }) : (
        <Box sx={{ px: 2, py: 2 }}>
          <Typography variant="body2" color="text.secondary">
            No languages available. Please add languages in your profile.
          </Typography>
        </Box>
      )}
    </Menu>
  );

  // Header row used at the top of both drawers.
  const drawerHeader = (
    <Toolbar sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box component="img" src={magellanLogoSextant} sx={{ width: 34, height: 34 }} alt="Magellan" />
      <Typography variant="h6" sx={{ fontFamily: '"Fraunces", Georgia, serif', fontWeight: 700, color: 'primary.main' }}>
        Magellan
      </Typography>
      <Box sx={{ flexGrow: 1 }} />
      {!isMobile && <IconButton onClick={toggleRail}><ChevronLeftIcon /></IconButton>}
    </Toolbar>
  );

  // Account actions (shown at the bottom of the mobile full menu).
  const accountItems = (
    <>
      <Divider textAlign="left"><Typography variant="caption" color="text.secondary">Account</Typography></Divider>
      <List>
        <ListItem button onClick={() => go('/profile')}>
          <ListItemIcon><ProfileIcon /></ListItemIcon><ListItemText primary="Profile" />
        </ListItem>
        {hasAdminAccess && (
          <ListItem button onClick={() => go('/admin')}>
            <ListItemIcon><AdminIcon /></ListItemIcon><ListItemText primary="Admin Site" />
          </ListItem>
        )}
        <ListItem button onClick={() => { setMenuOpen(false); logout(); }}>
          <ListItemIcon><LogoutIcon /></ListItemIcon><ListItemText primary="Logout" />
        </ListItem>
      </List>
    </>
  );

  return (
    <TutorProvider>
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar position="fixed" sx={{ zIndex: theme.zIndex.drawer + 1, backgroundColor: theme.palette.primary.main }}>
        <Toolbar sx={{ pl: '6px', pr: '10px' }}>
          {/* ☰ full menu (mobile) / rail toggle (desktop) */}
          <IconButton color="inherit" aria-label="menu" edge="start"
            onClick={() => (isMobile ? setMenuOpen(true) : toggleRail())} sx={{ mr: 0.5 }}>
            <MenuIcon />
          </IconButton>

          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Box component="img" src={magellanLogoSextant} sx={{ width: 40, height: 40, mr: 1 }} alt="Magellan" />
            <Typography variant="h6" component="div" sx={{
              fontWeight: 700, fontFamily: '"Fraunces", Georgia, serif',
              color: theme.palette.secondary.light, letterSpacing: '0.01em' }}>
              Magellan
            </Typography>
          </Box>

          <Box sx={{ flexGrow: 1 }} />

          <Typography variant="subtitle1" component="div"
            sx={{ display: { xs: 'none', sm: 'block' }, mr: 2, opacity: 0.9, fontWeight: 600 }}>
            {currentTitle()}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <Button onClick={handleLanguageMenuOpen} color="inherit" sx={{
              minWidth: 'auto', textTransform: 'none', display: 'flex', alignItems: 'center', gap: 1,
              px: 1.5, py: 0.5, borderRadius: 1, '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}>
              <Flag code={flagFor(selectedLanguage)} style={{ width: 20, height: 15 }} />
              <Typography variant="button" sx={{ fontWeight: 600 }}>{selectedLanguage?.toUpperCase() || 'EN'}</Typography>
              <ExpandMoreIcon fontSize="small" />
            </Button>

            <IconButton size="large" aria-label="account" aria-haspopup="true" onClick={handleUserMenuOpen} color="inherit">
              <Avatar sx={{ bgcolor: theme.palette.secondary.main, color: theme.palette.primary.main }}>
                {user?.first_name?.charAt(0) || 'U'}
              </Avatar>
            </IconButton>
            <Menu id="menu-appbar" anchorEl={anchorEl}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} keepMounted
              transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              open={Boolean(anchorEl)} onClose={handleUserMenuClose}>
              <MenuItem onClick={() => { handleUserMenuClose(); navigate('/progress'); }}>
                <ListItemIcon><MapIcon fontSize="small" /></ListItemIcon><ListItemText>Progress</ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={() => { handleUserMenuClose(); navigate('/profile'); }}>
                <ListItemIcon><ProfileIcon fontSize="small" /></ListItemIcon><ListItemText>Profile</ListItemText>
              </MenuItem>
              <Divider />
              {hasAdminAccess && (
                <MenuItem onClick={() => { handleUserMenuClose(); navigate('/admin'); }}>
                  <ListItemIcon><AdminIcon fontSize="small" /></ListItemIcon><ListItemText>Admin Site</ListItemText>
                </MenuItem>
              )}
              {hasAdminAccess && <Divider />}
              <MenuItem onClick={handleLogout}>
                <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon><ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Desktop persistent rail = full menu (everything, incl. account) */}
      {!isMobile && (
        <Drawer variant="persistent" open={railOpen}
          sx={{ width: drawerWidth, flexShrink: 0,
            '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' } }}>
          {drawerHeader}
          <Divider />
          <NavList items={fullMenu} />
          {accountItems}
        </Drawer>
      )}

      {/* Mobile full-menu drawer (opened by ☰) */}
      {isMobile && (
        <Drawer anchor="left" open={menuOpen} onClose={() => setMenuOpen(false)}
          sx={{ '& .MuiDrawer-paper': { width: drawerWidth, boxSizing: 'border-box' } }}>
          {drawerHeader}
          <Divider />
          <NavList items={fullMenu} />
          {accountItems}
        </Drawer>
      )}

      {/* Main content */}
      <Box component="main" sx={{
        flexGrow: 1, p: { xs: 2, md: 3 },
        width: !isMobile ? `calc(100% - ${railOpen ? drawerWidth : 0}px)` : '100%',
        marginLeft: !isMobile ? (railOpen ? 0 : `-${drawerWidth}px`) : 0,
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.sharp, duration: theme.transitions.duration.leavingScreen }) }}>
        <Toolbar />
        <Container maxWidth="lg" sx={{ mb: isMobile ? 10 : 4 }}>
          <Outlet />
          <TutorAssistant />
        </Container>
      </Box>

      {/* Bottom quick bar — mobile only */}
      {isMobile && (
        <BottomNavigation ref={bottomNavRef} value={speedValue()} onChange={(e, v) => go(speedNav[v].path)} showLabels
          sx={{ width: '100%', position: 'fixed', bottom: 0, zIndex: theme.zIndex.drawer + 2,
            borderTop: `1px solid ${theme.palette.divider}`, height: 'auto', minHeight: 56,
            pb: 'env(safe-area-inset-bottom, 0px)', boxSizing: 'content-box' }}>
          {speedNav.map((item) => (
            <BottomNavigationAction key={item.title} label={item.title}
              icon={item.center ? centerIcon(item.icon) : item.icon} />
          ))}
        </BottomNavigation>
      )}

      {languageMenu}
    </Box>
    </TutorProvider>
  );
};

export default Layout;
